/* Native observers only: no polling, media preloading or third-party dependencies. */
class VeloraFeatureRailV122 extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('ul');
    this.button = this.querySelector('button');
    if (!this.rail || !this.button) return;
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.schedule = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
    };
    this.rail.addEventListener('scroll', this.schedule, { passive: true, signal });
    this.button.addEventListener('click', () => {
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      this.rail.scrollTo({
        left: this.rail.scrollLeft >= max - 2 ? 0 : Math.min(max, this.rail.scrollLeft + this.rail.clientWidth * .85),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }, { signal });
    if ('ResizeObserver' in window) {
      this.resize = new ResizeObserver(this.schedule);
      this.resize.observe(this.rail);
      for (const item of this.rail.children) this.resize.observe(item);
    } else window.addEventListener('resize', this.schedule, { signal });
    this.update();
  }
  update() {
    const { clientWidth, scrollWidth, scrollLeft } = this.rail;
    const max = scrollWidth - clientWidth;
    this.button.hidden = max <= 2;
    const thumb = Math.min(100, clientWidth / (scrollWidth || 1) * 100);
    const progress = max > 0 ? Math.max(0, Math.min(1, scrollLeft / max)) : 0;
    this.style.setProperty('--rail-thumb', `${thumb}%`);
    this.style.setProperty('--rail-position', `${progress * (100 - thumb)}%`);
    const atEnd = max > 2 && scrollLeft >= max - 2;
    const english = this.dataset?.locale === 'en';
    this.querySelector('[data-rail-label]').textContent = atEnd
      ? (english ? 'Back to first features' : 'İlk özelliklere dön')
      : (english ? 'Explore more features' : 'Diğer özellikleri keşfet');
    this.querySelector('[data-rail-arrow]').textContent = atEnd ? '←' : '→';
  }
  disconnectedCallback() {
    this.abort?.abort(); this.resize?.disconnect(); cancelAnimationFrame(this.frame);
    this.frame = 0;
  }
}

const hintKey = 'velora.charmHint.v122';
let hintSeen = false;
class VeloraCharmHintV122 extends HTMLElement {
  connectedCallback() {
    // V156 keeps the persistent chain controls, without a timed photo overlay.
    if (document.body.classList.contains('lv-design-v156')) return;
    this.host = this.closest('velora-charm-customizer');
    this.stage = this.closest('[data-photo-stage]');
    if (!this.host || !this.stage || !('IntersectionObserver' in window)) return;
    try { hintSeen ||= sessionStorage.getItem(hintKey) === '1'; } catch { /* Private storage may be unavailable. */ }
    if (hintSeen) return;
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.host.addEventListener('click', (event) => {
      if (event.target.closest('[data-chain-trigger], [data-charm]')) this.dismiss();
    }, { capture: true, signal });
    this.stage.addEventListener('load', () => this.maybeShow(), { capture: true, signal });
    this.modes = new MutationObserver(() => {
      if (this.host.dataset.mode === 'chainless') {
        if (this.hasAttribute('data-visible')) this.dismiss();
      } else this.maybeShow();
    });
    this.modes.observe(this.host, { attributes: true, attributeFilter: ['data-mode'] });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.hasAttribute('data-visible')) this.dismiss();
      else this.maybeShow();
    }, { signal });
    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting && entry.intersectionRatio >= .55;
      if (!this.inView && this.hasAttribute('data-visible')) this.dismiss();
      else this.maybeShow();
    }, { threshold: [0, .55], rootMargin: '-140px 0px 0px 0px' });
    this.observer.observe(this.stage);
  }
  maybeShow() {
    const photo = this.stage.querySelector('[data-photo-current]');
    if (hintSeen || !this.inView || document.hidden || this.host.dataset.mode !== 'charm'
      || !photo?.complete || !photo.naturalWidth) return;
    this.markSeen();
    this.setAttribute('data-visible', '');
    this.timer = setTimeout(() => this.dismiss(), 4000);
  }
  markSeen() {
    hintSeen = true;
    try { sessionStorage.setItem(hintKey, '1'); } catch { /* In-memory fallback for this visit. */ }
  }
  dismiss() {
    this.markSeen();
    this.removeAttribute('data-visible');
    this.cleanup();
  }
  cleanup() {
    clearTimeout(this.timer); this.abort?.abort();
    this.observer?.disconnect(); this.modes?.disconnect();
  }
  disconnectedCallback() { this.removeAttribute('data-visible'); this.cleanup(); }
}
if (!customElements.get('lv-feature-rail-v122')) customElements.define('lv-feature-rail-v122', VeloraFeatureRailV122);
if (!customElements.get('lv-charm-hint-v122')) customElements.define('lv-charm-hint-v122', VeloraCharmHintV122);
